import React, { useEffect, useState } from "react";
import { ColDef } from "@ag-grid-community/core";

import { IQuestionOption } from "../../../types/questionOption";
import { IOption } from "../../../types/options";
import { IOptionDetail } from "../../../types/optionDetails";

import useUpdateOption from "../../../hooks/option/useUpdateQuestionOption";
import useCreateOption from "../../../hooks/option/useCreateOption";
import useDeleteOption from "../../../hooks/option/useDeleteOption";
import useDeleteOptionDetail from "../../../hooks/option_detail/useDeleteOptionDetail";

import { Icons } from '../../../data/Assets';

import { SVGIcon } from '../../wrappers/SVGIcon';
import DataTable from "../../reusables/DataTable";

interface IOptionProps {
  currOption: number[];
  setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
  questionOptions: IQuestionOption[];
  isDelete: boolean;
  setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteForOption: boolean;
  setIsDeleteForOption: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenDeleteModalForOption: React.Dispatch<React.SetStateAction<boolean>>;
}

const Option = ({
  currOption,
  setCurrOption,
  questionOptions,
  isDelete,
  setIsDelete,
  setIsOpenDeleteModal,
  isDeleteForOption,
  setIsDeleteForOption,
  setIsOpenDeleteModalForOption,
}: IOptionProps) => {
  const questionOptionIdx = currOption[0] || 0;
  const questionOptionId = questionOptions[questionOptionIdx].id || 0;
  const optionId = currOption[1] || 0;
  const [option, setOption] = useState(questionOptions[0].options[0]);
  const [title, setTitle] = useState<string>("");
  const [optionDetails, setOptionDetails] = useState<IOptionDetail[]>([]);
  const [optionDetail, setOptionDetail] = useState<any>(null);

  const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
  const [isServiceError, setIsServiceError] = useState<string | null>(null); //eslint-disable-line

  const blankOption: IOption = {
    title: "",
    question_option_id: questionOptionId ? questionOptionId : 0,
    option_details: [],
  };

  const blankOptionDetail: IOptionDetail = {
    option_id: option?.id ? option.id : 0,
    type: "",
    text: "",
  };

  useEffect(() => {
    if (optionId === questionOptions[questionOptionIdx].options.length) {
      setOption({ ...blankOption });
      setTitle("New Option");
      setOptionDetails([blankOptionDetail]);
    } else {
      setOption(questionOptions[questionOptionIdx].options[optionId]);
      setTitle(questionOptions[questionOptionIdx].options[optionId].title);
      setOptionDetails(
        questionOptions[questionOptionIdx].options[optionId].option_details
          ?.length
          ? [
            ...questionOptions[questionOptionIdx].options[optionId]
              .option_details,
          ]
          : [blankOptionDetail]
      );
    }
  }, [optionId, questionOptions]); //eslint-disable-line

  const { mutate: updateOption } = useUpdateOption({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: createOption } = useCreateOption({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: deleteOption } = useDeleteOption({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: deleteOptionDetail } = useDeleteOptionDetail();

  useEffect(() => {
    if (isDelete) {
      setCurrOption([questionOptionIdx, 0]);
      if (option.id) deleteOption(option.id);
      setIsDelete(false);
    }
  }, [isDelete]); //eslint-disable-line

  useEffect(() => {
    if (isDeleteForOption) {
      if (optionDetail?.data)
        handleDeleteOptionDetail(optionDetail?.rowIndex, optionDetail?.data);
      setIsDeleteForOption(false);
    }
  }, [isDeleteForOption]); //eslint-disable-line

  if (!option) {
    return <p>Loading...</p>;
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (optionId === questionOptions[questionOptionIdx].options.length) {
      createOption({
        question_option_id: questionOptionId,
        option_details: optionDetails,
        title: option.title,
      });
    } else {
      updateOption({
        id: option.id,
        question_option_id: questionOptionId,
        option_details: optionDetails,
        title: option.title,
      });
    }
    setTitle(option.title);
  };

  const handleDelete = () => {
    if (optionId === questionOptions[questionOptionIdx].options.length) {
      if (!optionId) {
        setCurrOption([questionOptionIdx]);
      } else {
        setCurrOption([questionOptionIdx, 0]);
      }
      setOption(questionOptions[questionOptionIdx].options[optionId]);
    } else {
      setIsOpenDeleteModal(true);
    }
  };

  const handleDeleteOptionDetail = (
    idx: number,
    optionDetail: IOptionDetail
  ) => {
    if (optionDetail.id) {
      deleteOptionDetail(optionDetail.id);
    }

    setOptionDetails([
      ...optionDetails.slice(0, idx),
      ...optionDetails.slice(idx + 1),
    ]);
  };

  const columns: ColDef[] = [
    {
      field: "type",
      headerName: "Type",
      editable: true,
      flex: 0.7,
      cellEditor: "agTextCellEditor",
    },
    {
      field: "text",
      headerName: "Text",
      editable: true,
      flex: 2,
      cellEditor: "agTextCellEditor",
    },
    {
      filter: false,
      headerName: "",
      flex: 0.2,
      cellRenderer: ({ data, rowIndex }: any) => {
        return (
          <div className="field__option_detail">
            <button
              onClick={() => {
                setIsOpenDeleteModalForOption(true);
                setOptionDetail({ data, rowIndex });
              }}
            >
              <SVGIcon src={Icons.Trash} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="imm_quest_admin__content">
      <div className="content_head">
        <h3>{title}</h3>
      </div>
      <div className="content_form">
        <div className="field">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            name="title"
            placeholder="Enter title"
            value={option.title}
            onChange={(e) => setOption({ ...option, title: e.target.value })}
          />
        </div>

        <label>Option Detail</label>
        <div className="ag_grid__main_content">
          <DataTable
            data={optionDetails}
            filter={false}
            columns={columns}
            paginationPageSize={10}
          />
        </div>
        <div className="option_detail__add_new">
          <button
            type="button"
            onClick={() =>
              setOptionDetails([
                ...optionDetails,
                { ...blankOptionDetail, option_id: option.id ?? 0 },
              ])
            }
          >
            Add New Option Detail
          </button>
        </div>
        <div className="actions">
          <button className="delete_btn" type="button" onClick={handleDelete}>
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : optionId ===
              questionOptions[questionOptionIdx].options.length ? (
              "Cancel"
            ) : (
              "Delete"
            )}
          </button>
          <button className="save_btn" type="button" onClick={handleSave}>
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : optionId ===
              questionOptions[questionOptionIdx].options.length ? (
              "Create"
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Option;
